import { DataRuleCache, processCondition } from '@msdk/common'
import {
  BaseFieldRule,
  CONTROL_VALIDATION_MAPPER,
  DEFAULT_MANDATORY_MSG,
  Entity,
  EntityContext,
  FieldRule,
  findNode,
  FormNode,
  formTraverse,
  GroupRule,
  ListValue,
  LogicalPath,
  RangeRule,
  TenantPath,
  UserPath,
} from '@msdk/definitions'

/**
 * The `runDataRules` function applies field rules to a form based on a set of conditions and evaluates
 * whether they should be applied.
 * @param {FormNode} root - The `root` parameter is the root node of the form. It represents the
 * top-level node in the form's hierarchy.
 * @param {EntityContext} entity - The `entity` parameter represents the current context or state of
 * the entity being processed. It contains the data and metadata related to the entity.
 * @param {DataRuleCache} cache - The `cache` parameter is an object that contains the rules and cached
 * functions for data validation. It has the following structure:
 * @param {boolean} [runMandatory=false] - The `runMandatory` parameter is a boolean flag that
 * determines whether to run mandatory field rules. If set to `true`, the function will also run
 * mandatory field rules in addition to regular field rules. If set to `false` or not provided, only
 * regular field rules will be run.
 */
export function runDataRules(root: FormNode, entity: EntityContext, cache: DataRuleCache, runMandatory = false) {
  cache.rules.forEach((r) => {
    const func = cache.cachedFunctions[processCondition(r.condition)]
    const res = evaluate(entity, func)
    if (res) {
      r.fieldRules.forEach((fr) => {
        const node = findNode(root, fr.field)
        node && applyFieldRule(fr, node as FormNode, entity, runMandatory)
      })
    }
  })
}

/**
 * The `runListDataRules` function iterates through a list of cached data rules and applies them to
 * each row in a list value, based on certain conditions.
 * @param {FormNode} root - The `root` parameter is the root node of the form. It represents the
 * top-level node in the form's hierarchy.
 * @param {EntityContext} entity - The `entity` parameter is an object that represents the current
 * entity being processed. It contains information about the fields and values of the entity.
 * @param {DataRuleCache} cache - The `cache` parameter is an object that contains cached data rules.
 * It has two properties:
 * @param {boolean} [runMandatory=false] - The `runMandatory` parameter is a boolean flag that
 * determines whether mandatory field rules should be executed. If `runMandatory` is set to `true`, the
 * function will execute mandatory field rules. If `runMandatory` is set to `false` or not provided,
 * the function will
 */
export function runListDataRules(root: FormNode, entity: EntityContext, cache: DataRuleCache, runMandatory = false) {
  Object.keys(cache.cachedListRules).forEach((k) => {
    const rules = cache.cachedListRules[k]
    const listValue = entity.fields[k] as ListValue
    if (listValue?.value?.length) {
      listValue.value.forEach((row) => {
        const rowNode = findNode(root, row.id) as FormNode
        const rowContext = new EntityContext(row, root, [])
        rules.forEach((r) => {
          const func = cache.cachedFunctions[processCondition(r.condition)]
          const res = evaluate(entity, func, rowContext)
          if (res) {
            r.fieldRules.forEach((fr) => {
              const fieldNode = findNode(rowNode, fr.field) as FormNode
              fieldNode && applyFieldRule(fr, fieldNode as FormNode, rowContext, runMandatory)
            })
          }
        })
      })
    }
  })

  Object.keys(cache.cachedOnOneRowOfListRules).forEach((k) => {
    const rules = cache.cachedOnOneRowOfListRules[k]
    const listName = rules[0].onOneRowOfList as string
    const listValue = entity.fields[listName] as ListValue
    if (listValue?.value?.length) {
      const rows = listValue.value as Entity[]
      rules.forEach((r) => {
        let result = false
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i]
          const rowContext = new EntityContext(row, root, [])
          const func = cache.cachedFunctions[processCondition(r.condition)]
          const res = evaluate(entity, func, rowContext)
          if (res) {
            result = true
            break
          }
        }

        if (result) {
          r.fieldRules.forEach((fr) => {
            const fieldNode = findNode(root, fr.field) as FormNode
            fieldNode && applyFieldRule(fr, fieldNode as FormNode, entity, runMandatory)
          })
        }
      })
    }
  })

  Object.keys(cache.cachedOnAllRowsOfList).forEach((k) => {
    const rules = cache.cachedOnAllRowsOfList[k]
    const listName = rules[0].onAllRowsOfList as string
    const listValue = entity.fields[listName] as ListValue
    if (listValue?.value?.length) {
      const rows = listValue.value as Entity[]
      rules.forEach((r) => {
        let result = true
        rows.forEach((row) => {
          const rowContext = new EntityContext(row, root, [])
          const func = cache.cachedFunctions[processCondition(r.condition)]
          const res = evaluate(entity, func, rowContext)
          result = result && res
        })

        if (result) {
          r.fieldRules.forEach((fr) => {
            const fieldNode = findNode(root, fr.field) as FormNode
            fieldNode && applyFieldRule(fr, fieldNode as FormNode, entity, runMandatory)
          })
        }
      })
    }
  })
}

/**
 * The function applies field rules to a form node based on the rule type and updates the node's value
 * state and validation messages accordingly.
 * @param {BaseFieldRule} fr - The parameter `fr` is of type `BaseFieldRule`, which is a base class for
 * different types of field rules. It can be one of the following types:
 * @param {FormNode} node - The `node` parameter represents a form node, which is a component in a form
 * that can have different states such as "Readonly", "Enabled", or "Invisible". It also contains a
 * value property that can hold the current value of the field and an array of validation messages.
 * @param {EntityContext} entity - The `entity` parameter is an object that represents the context of
 * the entity being processed. It likely contains information about the fields and their values in the
 * entity.
 * @param {boolean} runMandatory - The `runMandatory` parameter is a boolean value that determines
 * whether the mandatory rule should be applied or not. If `runMandatory` is `true`, the mandatory rule
 * will be applied to the field. If `runMandatory` is `false`, the mandatory rule will not be applied
 */
function applyFieldRule(fr: BaseFieldRule, node: FormNode, entity: EntityContext, runMandatory: boolean) {
  switch (fr.ruleType) {
    case 'FieldRule': {
      const fieldRule = fr as FieldRule
      if (fieldRule.readOnly) {
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Readonly']
        node.value.state = 'Readonly'
        propagateReaonlyChange(node, 'Readonly')
      } else if (fieldRule.readOnly === false) {
        node.value.state = 'Enabled'
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Readonly']
        propagateReaonlyChange(node, 'Enabled')
      }

      if (fieldRule.visible === false) {
        node.value.visibility = 'Invisible'
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Invisible']
        if (fieldRule.clearOnInvisible === true) {
          entity.fields[fieldRule.field] = null
        }
      } else if (fieldRule.visible === true) {
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Invisible']
        node.value.visibility = 'Visible'
      }

      const removeMandatoryMsg = () => {
        node.value.validationMessages = node.value.validationMessages.filter((m) => m !== DEFAULT_MANDATORY_MSG)
      }

      if (runMandatory) {
        if (fieldRule.mandatory === true) {
          node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Mandatory']
          if (!entity.fields[fieldRule.field] || entity.fields[fieldRule.field]?.isEmpty()) {
            //Remove any mandatory msg alread present and push the new one
            removeMandatoryMsg()
            node.value.validationMessages.push(DEFAULT_MANDATORY_MSG)
          }
        } else if (fieldRule.mandatory === false) {
          node.value.validationFlags &= ~CONTROL_VALIDATION_MAPPER['Mandatory']
          //Remove any mandatory msg since we are disabling the mandatory for this field
          removeMandatoryMsg()
        }
      }
      break
    }
    case 'RangeRule': {
      node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Range']
      const rangeRule = fr as RangeRule
      const range = node.value.range || { min: 0, max: 0 }
      range.min = rangeRule.min
      range.max = rangeRule.max
      node.value.range = range
      break
    }
    case 'GroupRule':
    case 'TabRule': {
      const groupRule = fr as GroupRule
      if (groupRule.readOnly) {
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Readonly']
        node.value.state = 'Readonly'
        propagateReaonlyChange(node, 'Readonly')
      } else if (groupRule.readOnly === false) {
        node.value.state = 'Enabled'
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Readonly']
        propagateReaonlyChange(node, 'Enabled')
      }

      if (groupRule.visible === false) {
        node.value.visibility = 'Invisible'
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Invisible']
        if (groupRule.clearOnInvisible) {
          const fields = entity.getFieldsInsideGroup(node.id)
          fields.forEach((f) => {
            entity.fields[f] = null
          })
        }
      } else if (groupRule.visible === true) {
        node.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Invisible']
        node.value.visibility = 'Visible'
      }
      break
    }
    case 'StepRule':
      break
    default:
      throw new Error(`rule of type ${fr.ruleType} is not supported`)
  }
}

/**
 * The evaluate function takes a context, a function, and an optional row context, and returns the
 * result of calling the function with the provided contexts.
 * @param {EntityContext} context - The `context` parameter represents the entity context, which
 * contains information about the current entity being evaluated.
 * @param {Function} func - The `func` parameter is a function that takes in an object with properties
 * `entity`, `row`, and `logic`. It is expected to return a boolean value.
 * @param {EntityContext} [rowContext] - The `rowContext` parameter is an optional parameter that
 * represents the context of a specific row in a dataset. It can be used to provide additional
 * information or context specific to that row when evaluating the function.
 * @returns a boolean value.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function evaluate(context: EntityContext, func: Function, rowContext?: EntityContext): boolean {
  const res = func({
    entity: context,
    row: rowContext,
    logic: new LogicalPath(),
    user: new UserPath(),
    tenant: new TenantPath(),
  })
  return res
}

/**
 * propagate a state change on the node to all the branch
 */
function propagateReaonlyChange(node: FormNode, state: 'Readonly' | 'Enabled') {
  formTraverse(node.firstChild, (n) => {
    n.value.validationFlags |= CONTROL_VALIDATION_MAPPER['Readonly']
    n.value.state = state
  })
}
